import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, ListGroup, Row, Spinner } from 'react-bootstrap';
import UserLayout from '../../components/user/UserLayout';
import api from '../../lib/api';
import { useToast } from '../../components/common/ToastProvider';
import { useSearchParams } from 'react-router-dom';

function formatTime(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

export default function UserAgentInboxPage() {
  const [inbox, setInbox] = useState({ pending: [], picked_to_me: [], picked_all: [], cancelled: [], agents: [], agent: null });
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [agentFilter, setAgentFilter] = useState('');
  const [loadingInbox, setLoadingInbox] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSupervisorMode = !!inbox?.supervisor_mode;

  const mergedItems = useMemo(() => {
    const pickedPool = isSupervisorMode ? (inbox.picked_all || []) : (inbox.picked_to_me || []);
    const pickedIds = new Set(pickedPool.map((h) => h.id));
    const pending = (inbox.pending || []).filter((h) => !pickedIds.has(h.id));
    const cancelled = (inbox.cancelled || []).filter((h) => !pickedIds.has(h.id));
    return [...pickedPool, ...pending, ...cancelled];
  }, [inbox, isSupervisorMode]);

  const loadInbox = async (selectedAgentId = agentFilter) => {
    try {
      setLoadingInbox(true);
      const params = selectedAgentId ? { agent_id: selectedAgentId } : {};
      const { data } = await api.get('/agent/handover/inbox/', { params });
      setInbox(data || { pending: [], picked_to_me: [], picked_all: [], cancelled: [], agents: [], agent: null });
      const requestedHandoverId = Number(searchParams.get('handover_id') || 0);
      if (requestedHandoverId) {
        const allRows = [...(data?.picked_all || []), ...(data?.picked_to_me || []), ...(data?.pending || []), ...(data?.cancelled || [])];
        const matched = allRows.find((row) => Number(row.id) === requestedHandoverId);
        if (matched) {
          setSelected(matched);
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('handover_id');
            return next;
          }, { replace: true });
          return;
        }
      }
      const firstPicked = (data?.supervisor_mode ? (data?.picked_all || []) : (data?.picked_to_me || []))?.[0];
      if (!selected && (firstPicked || data?.pending?.length || data?.cancelled?.length)) {
        const first = firstPicked || data.pending?.[0] || data.cancelled?.[0];
        setSelected(first);
      }
    } catch (e) {
      showToast(e?.response?.data?.error || 'Failed to load inbox', 'danger');
    } finally {
      setLoadingInbox(false);
    }
  };

  const loadConversation = async (handoverId) => {
    try {
      setLoadingThread(true);
      const { data } = await api.get(`/agent/handover/${handoverId}/conversation/`);
      setMessages(data?.messages || []);
      setSelected(data?.handover || selected);
    } catch (e) {
      showToast(e?.response?.data?.error || 'Failed to load conversation', 'danger');
    } finally {
      setLoadingThread(false);
    }
  };

  useEffect(() => {
    api.post('/handover/notifications/', { mark_all: true }, { suppressToast: true }).catch(() => {});
    loadInbox();
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      loadInbox(agentFilter);
    }, 15000);
    return () => clearInterval(t);
  }, [agentFilter]);

  useEffect(() => {
    if (selected?.id) {
      loadConversation(selected.id);
    }
  }, [selected?.id]);

  const pick = async (handoverId) => {
    if (isSupervisorMode) {
      showToast('Supervisor mode cannot pick chats directly. Use pick controls from the admin handover queue.', 'warning');
      return;
    }
    try {
      await api.post(`/agent/handover/${handoverId}/pick/`, {});
      showToast('Chat picked successfully', 'success');
      await loadInbox();
      await loadConversation(handoverId);
    } catch (e) {
      showToast(e?.response?.data?.error || 'Could not pick chat', 'danger');
    }
  };

  const sendReply = async () => {
    const message = reply.trim();
    if (!message || !selected?.id) return;
    if (isSupervisorMode) {
      showToast('Supervisor mode cannot send direct agent replies.', 'warning');
      return;
    }
    try {
      setSending(true);
      await api.post(`/agent/handover/${selected.id}/reply/`, { message });
      setReply('');
      await loadConversation(selected.id);
      await loadInbox();
      showToast('Reply sent', 'success');
    } catch (e) {
      showToast(e?.response?.data?.error || 'Failed to send reply', 'danger');
    } finally {
      setSending(false);
    }
  };

  const resolve = async () => {
    if (!selected?.id) return;
    try {
      await api.post(`/handover/${selected.id}/resolve/`, {});
      showToast('Handover resolved', 'success');
      setSelected(null);
      setMessages([]);
      await loadInbox();
    } catch (e) {
      showToast(e?.response?.data?.error || 'Failed to resolve', 'danger');
    }
  };

  return (
    <UserLayout title="Live Agent Inbox" subtitle="Pick handovers and continue conversations from a single responsive chat interface.">
      {isSupervisorMode ? (
        <Card className="user-card p-3 mb-3">
          <Form.Group>
            <Form.Label className="mb-1">View activity by agent</Form.Label>
            <Form.Select
              value={agentFilter}
              onChange={(e) => {
                const next = e.target.value;
                setAgentFilter(next);
                setSelected(null);
                setMessages([]);
                loadInbox(next);
              }}
            >
              <option value="">All agents</option>
              {(inbox.agents || []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.email})
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Card>
      ) : null}
      <Row className="g-3">
        <Col lg={4}>
          <Card className="user-card p-0 h-100">
            <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
              <div className="fw-semibold">Open Handovers</div>
              <Badge bg="primary">{mergedItems.length}</Badge>
            </div>
            <div style={{ maxHeight: '68vh', overflowY: 'auto' }}>
              {loadingInbox ? (
                <div className="text-center p-4"><Spinner animation="border" /></div>
              ) : (
                <ListGroup variant="flush">
                  {mergedItems.map((item) => {
                    const mine = (inbox.picked_to_me || []).some((a) => a.id === item.id);
                    const assigned = item.status === 'picked' || item.status === 'active';
                    return (
                      <ListGroup.Item
                        action
                        key={item.id}
                        active={selected?.id === item.id}
                        onClick={() => setSelected(item)}
                      >
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div>
                            <div className="fw-semibold">#{item.id} · {item.channel}</div>
                            <div className="small text-muted">{item.requester_name || item.requester_email || 'Guest'}</div>
                            <div className="small text-muted">{formatTime(item.created_at)}</div>
                            {item.picked_by_name ? (
                              <div className="small text-muted">Picked by: {item.picked_by_name} ({item.picked_by_email || 'n/a'})</div>
                            ) : null}
                          </div>
                          <Badge bg={item.status === 'cancelled' ? 'secondary' : mine ? 'success' : assigned ? 'info' : 'warning'}>
                            {item.status === 'cancelled' ? 'Cancelled' : mine ? 'Picked by me' : assigned ? 'Picked/Active' : 'Pending'}
                          </Badge>
                        </div>
                      </ListGroup.Item>
                    );
                  })}
                  {!mergedItems.length && <ListGroup.Item className="text-muted">No handovers.</ListGroup.Item>}
                </ListGroup>
              )}
            </div>
          </Card>
        </Col>

        <Col lg={8}>
          <Card className="user-card h-100 d-flex flex-column">
            <div className="p-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <div className="fw-semibold">{selected ? `Thread: ${selected.thread_id || '-'}` : 'Select a chat'}</div>
                  <div className="small text-muted">{selected?.requester_email || selected?.requester_name || ''}</div>
                  {selected?.picked_by_name ? (
                    <div className="small text-muted">Picked by: {selected.picked_by_name} ({selected.picked_by_email || 'n/a'})</div>
                  ) : null}
                </div>
              <div className="d-flex gap-2">
                {selected && selected.status === 'pending' && !isSupervisorMode && (
                  <Button size="sm" onClick={() => pick(selected.id)}>Pick Chat</Button>
                )}
                {selected && !['resolved', 'cancelled'].includes(selected.status) && (
                  <Button size="sm" variant="outline-success" onClick={resolve}>Resolve</Button>
                )}
              </div>
            </div>

            <div className="p-3" style={{ flex: 1, minHeight: 360, maxHeight: '52vh', overflowY: 'auto', background: '#f8fafc' }}>
              {loadingThread ? <div className="text-center"><Spinner animation="border" /></div> : (
                <div className="d-flex flex-column gap-2">
                  {messages.map((m) => (
                    <div
                      key={m.id}
                      className="p-2 rounded"
                      style={{
                        maxWidth: '85%',
                        alignSelf: m.role === 'user' ? 'flex-start' : 'flex-end',
                        background: m.role === 'user' ? '#fff' : (m.role === 'assistant' ? '#e2e8f0' : '#dbeafe'),
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <div className="small text-muted text-uppercase mb-1">{m.role}</div>
                      <div>{m.text}</div>
                      <div className="small text-muted mt-1">{formatTime(m.created_at)}</div>
                    </div>
                  ))}
                  {!messages.length && <div className="text-muted">No messages yet.</div>}
                </div>
              )}
            </div>

            <div className="p-3 border-top">
              <Form onSubmit={(e) => { e.preventDefault(); sendReply(); }}>
                <div className="d-flex gap-2">
                  <Form.Control
                    placeholder={selected ? 'Type a human-agent reply...' : 'Pick a conversation to reply'}
                    value={reply}
                    disabled={!selected || ['resolved', 'cancelled'].includes(selected.status) || isSupervisorMode}
                    onChange={(e) => setReply(e.target.value)}
                  />
                  <Button type="submit" disabled={sending || !selected || ['resolved', 'cancelled'].includes(selected.status) || isSupervisorMode}>
                    Send
                  </Button>
                </div>
              </Form>
            </div>
          </Card>
        </Col>
      </Row>
    </UserLayout>
  );
}
